import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type {
  CTSCalculoResult,
  DepositoCTS,
  GratificacionCalculoResult,
  ProvisionMensualResult,
  ProvisionMensual,
  Vacacion,
  VacacionAlertaVencimiento,
  VacacionResumenTrabajador,
  RegistrarGoceRequest,
  Prestamo,
  PrestamoCuota,
} from '../types/beneficios';
import { BeneficiosService } from '../services/beneficiosService';

interface BeneficiosStore {
  // State
  loading: boolean;
  error: string | null;

  // CTS
  ctsResultados: CTSCalculoResult[];
  depositosCTS: DepositoCTS[];

  // Gratificaciones
  gratResultados: GratificacionCalculoResult[];

  // Provisiones
  provisionResultados: ProvisionMensualResult[];
  provisiones: ProvisionMensual[];

  // Vacaciones
  vacaciones: Vacacion[];
  alertasVencimiento: VacacionAlertaVencimiento[];
  resumenVacaciones: VacacionResumenTrabajador[];
  cuentaCorriente: VacacionResumenTrabajador | null;

  // Préstamos
  prestamos: Prestamo[];
  cuotasSeleccionadas: PrestamoCuota[];

  // Tab activo
  activeTab: number;

  // Actions
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setActiveTab: (tab: number) => void;

  // CTS Actions
  calcularCTS: (semestre: number, anio: number) => Promise<void>;
  grabarCTS: (semestre: number, anio: number, idPeriodo: number) => Promise<void>;
  loadDepositosCTS: (anio?: number, idTrabajador?: number) => Promise<void>;

  // Gratificación Actions
  calcularGratificacion: (semestre: number, anio: number) => Promise<void>;

  // Provisiones Actions
  calcularProvisiones: (idPeriodo: number) => Promise<void>;
  grabarProvisiones: (idPeriodo: number) => Promise<void>;
  loadProvisiones: (idPeriodo?: number) => Promise<void>;

  // Vacaciones Actions
  loadVacaciones: (idTrabajador: number) => Promise<void>;
  guardarVacacion: (vacacion: Vacacion) => Promise<void>;
  generarPeriodos: (idTrabajador: number) => Promise<number>;
  registrarGoce: (request: RegistrarGoceRequest, idTrabajador: number) => Promise<void>;
  eliminarGoce: (idVacacionGoce: number, idTrabajador: number) => Promise<void>;
  loadAlertasVencimiento: (diasUmbral?: number) => Promise<void>;
  loadResumenVacaciones: () => Promise<void>;
  loadCuentaCorriente: (idTrabajador: number) => Promise<void>;

  // Préstamos Actions
  loadPrestamos: (idTrabajador?: number, estado?: string) => Promise<void>;
  crearPrestamo: (prestamo: Prestamo) => Promise<void>;
  anularPrestamo: (idPrestamo: number) => Promise<void>;
  loadCuotas: (idPrestamo: number) => Promise<void>;
  updateCuota: (idCuota: number, monto: number, fechaProgramada: string, idPrestamo: number) => Promise<void>;
  reprogramarCuotas: (idPrestamo: number, cuotasAdicionales: number) => Promise<void>;
}

export const useBeneficiosStore = create<BeneficiosStore>()(
  devtools(
    (set) => ({
      // Initial state
      loading: false,
      error: null,
      ctsResultados: [],
      depositosCTS: [],
      gratResultados: [],
      provisionResultados: [],
      provisiones: [],
      vacaciones: [],
      alertasVencimiento: [],
      resumenVacaciones: [],
      cuentaCorriente: null,
      prestamos: [],
      cuotasSeleccionadas: [],
      activeTab: 0,

      // Base actions
      setLoading: (loading) => set({ loading }),
      setError: (error) => set({ error }),
      setActiveTab: (tab) => set({ activeTab: tab }),

      // CTS
      calcularCTS: async (semestre, anio) => {
        set({ loading: true, error: null });
        try {
          const resultados = await BeneficiosService.calcularCTS(semestre, anio);
          set({ ctsResultados: resultados });
        } catch {
          set({ error: 'Error calculando CTS' });
        } finally {
          set({ loading: false });
        }
      },

      grabarCTS: async (semestre, anio, idPeriodo) => {
        set({ loading: true, error: null });
        try {
          const depositos = await BeneficiosService.grabarCTS({ semestre, anio, idPeriodo });
          set((state) => ({ depositosCTS: [...depositos, ...state.depositosCTS] }));
        } catch {
          set({ error: 'Error grabando depósitos CTS' });
        } finally {
          set({ loading: false });
        }
      },

      loadDepositosCTS: async (anio, idTrabajador) => {
        set({ loading: true, error: null });
        try {
          const depositos = await BeneficiosService.getDepositosCTS(anio, idTrabajador);
          set({ depositosCTS: depositos });
        } catch {
          set({ error: 'Error cargando depósitos CTS' });
        } finally {
          set({ loading: false });
        }
      },

      // Gratificaciones
      calcularGratificacion: async (semestre, anio) => {
        set({ loading: true, error: null });
        try {
          const resultados = await BeneficiosService.calcularGratificacion(semestre, anio);
          set({ gratResultados: resultados });
        } catch {
          set({ error: 'Error calculando gratificación' });
        } finally {
          set({ loading: false });
        }
      },

      // Provisiones
      calcularProvisiones: async (idPeriodo) => {
        set({ loading: true, error: null });
        try {
          const resultados = await BeneficiosService.calcularProvisiones(idPeriodo);
          set({ provisionResultados: resultados });
        } catch {
          set({ error: 'Error calculando provisiones' });
        } finally {
          set({ loading: false });
        }
      },

      grabarProvisiones: async (idPeriodo) => {
        set({ loading: true, error: null });
        try {
          await BeneficiosService.grabarProvisiones(idPeriodo);
          const provisiones = await BeneficiosService.getProvisiones(idPeriodo);
          set({ provisiones });
        } catch {
          set({ error: 'Error grabando provisiones' });
        } finally {
          set({ loading: false });
        }
      },

      loadProvisiones: async (idPeriodo) => {
        set({ loading: true, error: null });
        try {
          const provisiones = await BeneficiosService.getProvisiones(idPeriodo);
          set({ provisiones });
        } catch {
          set({ error: 'Error cargando provisiones' });
        } finally {
          set({ loading: false });
        }
      },

      // Vacaciones
      loadVacaciones: async (idTrabajador) => {
        set({ loading: true, error: null });
        try {
          const vacaciones = await BeneficiosService.getVacaciones(idTrabajador);
          set({ vacaciones });
        } catch {
          set({ error: 'Error cargando vacaciones' });
        } finally {
          set({ loading: false });
        }
      },

      guardarVacacion: async (vacacion) => {
        set({ loading: true, error: null });
        try {
          if (vacacion.idVacacion === 0) {
            await BeneficiosService.registrarVacacion(vacacion);
          } else {
            await BeneficiosService.actualizarVacacion(vacacion);
          }
          const vacaciones = await BeneficiosService.getVacaciones(vacacion.idTrabajador);
          set({ vacaciones });
        } catch {
          set({ error: 'Error guardando vacación' });
        } finally {
          set({ loading: false });
        }
      },

      generarPeriodos: async (idTrabajador) => {
        set({ loading: true, error: null });
        try {
          const nuevos = await BeneficiosService.generarPeriodos(idTrabajador);
          const vacaciones = await BeneficiosService.getVacaciones(idTrabajador);
          set({ vacaciones });
          return nuevos.length;
        } catch {
          set({ error: 'Error generando periodos vacacionales' });
          return -1;
        } finally {
          set({ loading: false });
        }
      },

      registrarGoce: async (request, idTrabajador) => {
        set({ loading: true, error: null });
        try {
          await BeneficiosService.registrarGoce(request);
          const vacaciones = await BeneficiosService.getVacaciones(idTrabajador);
          set({ vacaciones });
        } catch {
          set({ error: 'Error registrando goce/venta' });
        } finally {
          set({ loading: false });
        }
      },

      eliminarGoce: async (idVacacionGoce, idTrabajador) => {
        set({ loading: true, error: null });
        try {
          await BeneficiosService.eliminarGoce(idVacacionGoce);
          const vacaciones = await BeneficiosService.getVacaciones(idTrabajador);
          set({ vacaciones });
        } catch {
          set({ error: 'Error eliminando goce/venta' });
        } finally {
          set({ loading: false });
        }
      },

      loadAlertasVencimiento: async (diasUmbral) => {
        set({ loading: true, error: null });
        try {
          const alertas = await BeneficiosService.getVacacionesPorVencer(diasUmbral);
          set({ alertasVencimiento: alertas });
        } catch {
          set({ error: 'Error cargando alertas de vencimiento' });
        } finally {
          set({ loading: false });
        }
      },

      loadResumenVacaciones: async () => {
        set({ loading: true, error: null });
        try {
          const resumen = await BeneficiosService.getResumenTodos();
          set({ resumenVacaciones: resumen });
        } catch {
          set({ error: 'Error cargando resumen de vacaciones' });
        } finally {
          set({ loading: false });
        }
      },

      loadCuentaCorriente: async (idTrabajador) => {
        set({ loading: true, error: null });
        try {
          const cuenta = await BeneficiosService.getCuentaCorriente(idTrabajador);
          set({ cuentaCorriente: cuenta, vacaciones: cuenta.periodos });
        } catch {
          set({ error: 'Error cargando cuenta corriente' });
        } finally {
          set({ loading: false });
        }
      },

      // Préstamos
      loadPrestamos: async (idTrabajador, estado) => {
        set({ loading: true, error: null });
        try {
          const prestamos = await BeneficiosService.getPrestamos(idTrabajador, estado);
          set({ prestamos });
        } catch {
          set({ error: 'Error cargando préstamos' });
        } finally {
          set({ loading: false });
        }
      },

      crearPrestamo: async (prestamo) => {
        set({ loading: true, error: null });
        try {
          await BeneficiosService.crearPrestamo(prestamo);
          const prestamos = await BeneficiosService.getPrestamos();
          set({ prestamos });
        } catch {
          set({ error: 'Error creando préstamo' });
        } finally {
          set({ loading: false });
        }
      },

      anularPrestamo: async (idPrestamo) => {
        set({ loading: true, error: null });
        try {
          await BeneficiosService.anularPrestamo(idPrestamo);
          const prestamos = await BeneficiosService.getPrestamos();
          set({ prestamos });
        } catch {
          set({ error: 'Error anulando préstamo' });
        } finally {
          set({ loading: false });
        }
      },

      loadCuotas: async (idPrestamo) => {
        try {
          const cuotas = await BeneficiosService.getCuotas(idPrestamo);
          set({ cuotasSeleccionadas: cuotas });
        } catch {
          set({ error: 'Error cargando cuotas' });
        }
      },

      updateCuota: async (idCuota, monto, fechaProgramada, idPrestamo) => {
        set({ loading: true, error: null });
        try {
          await BeneficiosService.updateCuota(idCuota, monto, fechaProgramada);
          const cuotas = await BeneficiosService.getCuotas(idPrestamo);
          set({ cuotasSeleccionadas: cuotas });
          const prestamos = await BeneficiosService.getPrestamos();
          set({ prestamos });
        } catch {
          set({ error: 'Error actualizando cuota' });
        } finally {
          set({ loading: false });
        }
      },

      reprogramarCuotas: async (idPrestamo, cuotasAdicionales) => {
        set({ loading: true, error: null });
        try {
          await BeneficiosService.reprogramarCuotas(idPrestamo, cuotasAdicionales);
          const cuotas = await BeneficiosService.getCuotas(idPrestamo);
          set({ cuotasSeleccionadas: cuotas });
          const prestamos = await BeneficiosService.getPrestamos();
          set({ prestamos });
        } catch {
          set({ error: 'Error reprogramando cuotas' });
        } finally {
          set({ loading: false });
        }
      },
    }),
    { name: 'BeneficiosStore' }
  )
);
